/**
 * Shared UI primitives + design tokens reused across pages.
 * Extracted from Leads.jsx so Follow-Ups and Companies can use them
 * without duplicating the color maps and popover plumbing.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, Check, X, ChevronDown, AlertTriangle, Loader2, Clock, UserCircle2 } from 'lucide-react'
import { api } from '../lib/api.js'
import { useEmployees, employeeLabel, employeeRecord } from '../lib/employees.js'
import { useDoctors, doctorRecord } from '../lib/doctors.js'

/* ─────────────────────── design tokens ─────────────────────── */

export const CARD_BASE = 'bg-white rounded-2xl ring-1 ring-zinc-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
export const CARD      = `${CARD_BASE} overflow-hidden`

export const STAGES = [
  'รอติดต่อ', 'รอติดต่อกลับ', 'คุยกับ HR แล้ว',
  'Follow up', 'Done', 'Cancel', 'ไม่รับสาย',
]
export const STAGE_DOT = {
  'รอติดต่อ':                'bg-zinc-400',
  'รอติดต่อกลับ':    'bg-amber-500',
  'คุยกับ HR แล้ว':            'bg-blue-500',
  'Follow up':              'bg-violet-500',
  'Done':                   'bg-emerald-500',
  'Cancel':                 'bg-rose-500',
  'ไม่รับสาย':                'bg-orange-500',
}
export const STAGE_BADGE = {
  'รอติดต่อ':                'bg-zinc-50 ring-zinc-200/70 text-zinc-700',
  'รอติดต่อกลับ':    'bg-amber-50 ring-amber-200/80 text-amber-900',
  'คุยกับ HR แล้ว':            'bg-blue-50 ring-blue-200/80 text-blue-800',
  'Follow up':              'bg-violet-50 ring-violet-200/80 text-violet-800',
  'Done':                   'bg-emerald-50 ring-emerald-200/80 text-emerald-800',
  'Cancel':                 'bg-rose-50 ring-rose-200/80 text-rose-800',
  'ไม่รับสาย':                'bg-orange-50 ring-orange-200/80 text-orange-800',
}

export const CHANNELS = ['call', 'email', 'line', 'meeting', 'visit', 'other']
export const CHANNEL_DOT = {
  call:    'bg-blue-500',
  email:   'bg-violet-500',
  line:    'bg-emerald-500',
  meeting: 'bg-amber-500',
  visit:   'bg-teal-500',
  other:   'bg-zinc-400',
}

export const PROVINCES = [
  'กรุงเทพมหานคร','กระบี่','กาญจนบุรี','กาฬสินธุ์','กำแพงเพชร',
  'ขอนแก่น','จันทบุรี','ฉะเชิงเทรา','ชลบุรี','ชัยนาท',
  'ชัยภูมิ','ชุมพร','เชียงราย','เชียงใหม่','ตรัง',
  'ตราด','ตาก','นครนายก','นครปฐม','นครพนม',
  'นครราชสีมา','นครศรีธรรมราช','นครสวรรค์','นนทบุรี','นราธิวาส',
  'น่าน','บึงกาฬ','บุรีรัมย์','ปทุมธานี','ประจวบคีรีขันธ์',
  'ปราจีนบุรี','ปัตตานี','พระนครศรีอยุธยา','พะเยา','พังงา',
  'พัทลุง','พิจิตร','พิษณุโลก','เพชรบุรี','เพชรบูรณ์',
  'แพร่','ภูเก็ต','มหาสารคาม','มุกดาหาร','แม่ฮ่องสอน',
  'ยโสธร','ยะลา','ร้อยเอ็ด','ระนอง','ระยอง',
  'ราชบุรี','ลพบุรี','ลำปาง','ลำพูน','เลย',
  'ศรีสะเกษ','สกลนคร','สงขลา','สตูล','สมุทรปราการ',
  'สมุทรสงคราม','สมุทรสาคร','สระแก้ว','สระบุรี','สิงห์บุรี',
  'สุโขทัย','สุพรรณบุรี','สุราษฎร์ธานี','สุรินทร์','หนองคาย',
  'หนองบัวลำภู','อ่างทอง','อำนาจเจริญ','อุดรธานี','อุตรดิตถ์',
  'อุทัยธานี','อุบลราชธานี',
]
export const BILLING_RIGHTS = ['ปกส.', 'บัตรทอง', 'ประกันกลุ่ม', 'สวัสดิการ', 'อื่นๆ']


/* ─────────────────────── small form primitives ─────────────────────── */

export function Field({ label, hint, hintInline, icon: Icon, children }) {
  return (
    <div>
      <label className="block mb-1.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium text-zinc-500">
          {Icon && <Icon size={11} className="text-zinc-400" />}
          {label}
          {hintInline && (
            <span className="text-zinc-400 normal-case tracking-normal font-normal">({hintInline})</span>
          )}
        </span>
        {hint && <span className="block text-[11px] text-zinc-400 mt-0.5 normal-case tracking-normal font-normal">{hint}</span>}
      </label>
      {children}
    </div>
  )
}

export function SingleChips({ options, value, onChange, dotMap, allowClear }) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((o) => {
        const on = value === o
        return (
          <button
            key={o} type="button"
            onClick={() => onChange(allowClear && on ? '' : o)}
            className={
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12.5px] font-medium border transition-colors ' +
              (on ? 'bg-zinc-900 border-zinc-900 text-white'
                  : 'bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300')
            }
          >
            {dotMap && <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-white' : dotMap[o]}`} />}
            {o}
          </button>
        )
      })}
    </div>
  )
}

export function MultiChips({ options, value, onChange, dotMap }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((o) => {
        const on = value.includes(o)
        return (
          <button key={o} type="button" onClick={() => toggle(o)}
            className={
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12.5px] font-medium border transition-colors ' +
              (on ? 'bg-amber-100 border-amber-300 text-amber-900'
                  : 'bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300')
            }>
            {dotMap && <span className={`w-1.5 h-1.5 rounded-full ${dotMap[o]}`} />}
            {o}
          </button>
        )
      })}
    </div>
  )
}


/* ─────────────────────── company picker ─────────────────────── */

export function CompanyCombobox({ value, onChange, placeholder = 'Select company…', disabled }) {
  const [open, setOpen]   = useState(false)
  const [q, setQ]         = useState('')
  const [results, setResults] = useState([])
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    // Server now returns { rows, total, limit, offset }; older shape was a
    // plain array, so accept both for resilience.
    api.companies.list(q)
      .then((res) => { if (!cancelled) setResults(Array.isArray(res) ? res : (res?.rows || [])) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [open, q])

  useEffect(() => {
    if (!open) return
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onEsc   = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] hover:border-zinc-300 focus:border-amber-500 focus:outline-none text-left"
      >
        <span className={value ? 'text-zinc-900 truncate' : 'text-zinc-400'}>
          {value?.name || placeholder}
        </span>
        {value && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onChange(null) }}
            className="text-zinc-400 hover:text-zinc-700"
            aria-label="Clear"
          >
            <X size={13} />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-white rounded-xl ring-1 ring-zinc-200 shadow-[0_8px_28px_-6px_rgba(0,0,0,0.12)] overflow-hidden">
          <div className="relative border-b border-zinc-100">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="พิมพ์ชื่อบริษัท…"
              className="w-full pl-9 pr-3 py-2 text-[13px] focus:outline-none"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {results.length === 0 && (
              <li className="px-3 py-4 text-[12.5px] text-zinc-400 text-center">No companies found.</li>
            )}
            {results.map((c) => {
              const selected = value?.id === c.id
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => { onChange({ id: c.id, name: c.name }); setOpen(false); setQ('') }}
                    className={'w-full text-left px-3 py-1.5 text-[13px] flex items-center justify-between gap-2 ' +
                      (selected ? 'bg-amber-50 text-amber-900' : 'text-zinc-700 hover:bg-zinc-50')}
                  >
                    <span className="truncate">{c.name}</span>
                    {selected && <Check size={13} className="text-amber-600 shrink-0" />}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}


/* ─────────────────────── generic static-list combobox (e.g. provinces) ─────────────────────── */

export function Combobox({ options, value, onChange, placeholder = 'Select…', clearable = true, disabled }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onEsc   = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const filtered = q
    ? options.filter((o) => o.toLowerCase().includes(q.toLowerCase()))
    : options

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] hover:border-zinc-300 focus:border-amber-500 focus:outline-none text-left"
      >
        <span className={value ? 'text-zinc-900 truncate' : 'text-zinc-400'}>
          {value || placeholder}
        </span>
        {clearable && value && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onChange('') }}
            className="text-zinc-400 hover:text-zinc-700"
            aria-label="Clear"
          >
            <X size={13} />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-white rounded-xl ring-1 ring-zinc-200 shadow-[0_8px_28px_-6px_rgba(0,0,0,0.12)] overflow-hidden">
          <div className="relative border-b border-zinc-100">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ค้นหา…"
              className="w-full pl-9 pr-3 py-2 text-[13px] focus:outline-none"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-[12.5px] text-zinc-400 text-center">No matches.</li>
            )}
            {filtered.map((o) => {
              const selected = value === o
              return (
                <li key={o}>
                  <button
                    type="button"
                    onClick={() => { onChange(o); setOpen(false); setQ('') }}
                    className={'w-full text-left px-3 py-1.5 text-[13px] flex items-center justify-between gap-2 ' +
                      (selected ? 'bg-amber-50 text-amber-900' : 'text-zinc-700 hover:bg-zinc-50')}
                  >
                    <span className="truncate">{o}</span>
                    {selected && <Check size={13} className="text-amber-600 shrink-0" />}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}


/* ─────────────────────── follow-up list card (shared by Lead + Company drawers) ─────────────────────── */

export function FollowUpItemList({ followUps, onPick, emptyHint = 'No follow-ups yet.' }) {
  if (followUps == null) {
    return <div className="text-[12.5px] text-zinc-400 py-4 text-center">Loading…</div>
  }
  if (followUps.length === 0) {
    return (
      <div className="px-3 py-6 rounded-xl border border-dashed border-zinc-200 text-center text-[12.5px] text-zinc-400">
        {emptyHint}
      </div>
    )
  }
  return (
    <ul className="space-y-1.5">
      {followUps.map((f) => (
        <li key={f.id}>
          <button
            onClick={() => onPick?.(f)}
            className="w-full text-left px-3 py-2 rounded-lg border border-zinc-200 hover:border-amber-300 hover:bg-amber-50/30 transition-colors"
          >
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <span className="text-[12px] font-semibold tabular-nums text-zinc-700">{fmtFuDate(f.date)}</span>
              <div className="flex items-center gap-1.5">
                {f.channel && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-50 ring-1 ring-zinc-200/70 text-[10.5px] font-medium text-zinc-700">
                    <span className={`w-1 h-1 rounded-full ${CHANNEL_DOT[f.channel] || 'bg-zinc-400'}`} />
                    {f.channel}
                  </span>
                )}
                {f.outcome_stage && (
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ring-1 text-[10.5px] font-medium ${STAGE_BADGE[f.outcome_stage] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700'}`}>
                    <span className={`w-1 h-1 rounded-full ${STAGE_DOT[f.outcome_stage] || 'bg-zinc-400'}`} />
                    {f.outcome_stage}
                  </span>
                )}
              </div>
            </div>
            <div className="text-[12.5px] text-zinc-600 line-clamp-2">
              {f.note || <span className="text-zinc-300 italic">(no note)</span>}
            </div>
          </button>
        </li>
      ))}
    </ul>
  )
}

function fmtFuDate(unix) {
  if (!unix) return '—'
  const d = new Date(unix * 1000)
  return d.toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })
}

/* ─────────────────────── filter toolbar primitives ─────────────────────── */

export function FilterButton({ icon: Icon, label, count, children }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const active = count > 0
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={
          'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium border transition-colors ' +
          (active
            ? 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'
            : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300')
        }
      >
        <Icon size={13.5} strokeWidth={2.25} className={active ? 'text-amber-600' : 'text-zinc-500'} />
        {label}
        {active && (
          <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber-500 text-white text-[10.5px] font-semibold tabular-nums">
            {count}
          </span>
        )}
        <ChevronDown size={12} className={'transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>

      {open && (
        <div className="absolute z-20 top-full mt-2 left-0 w-[calc(100vw-2rem)] sm:w-auto min-w-0 sm:min-w-[260px] max-w-[320px] bg-white rounded-xl ring-1 ring-zinc-200 shadow-[0_8px_28px_-6px_rgba(0,0,0,0.12)] p-1">
          {children}
        </div>
      )}
    </div>
  )
}

export function ChipPanel({ options, value, onChange, dotMap }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
  const allOn  = options.length > 0 && value.length === options.length
  const noneOn = value.length === 0
  return (
    <div>
      <div className="flex items-center justify-between px-3 pt-2 pb-1.5 border-b border-zinc-100">
        <button onClick={() => onChange(options.slice())} disabled={allOn}
          className="text-[11.5px] font-medium text-amber-600 hover:text-amber-700 disabled:text-zinc-300 disabled:cursor-not-allowed">
          Select all
        </button>
        <span className="text-[11px] text-zinc-400 tabular-nums">{value.length}/{options.length} selected</span>
        <button onClick={() => onChange([])} disabled={noneOn}
          className="text-[11.5px] font-medium text-zinc-500 hover:text-zinc-900 disabled:text-zinc-300 disabled:cursor-not-allowed">
          Clear
        </button>
      </div>
      <div className="p-1">
        {options.map((o) => {
          const on = value.includes(o)
          return (
            <button key={o} onClick={() => toggle(o)}
              className={'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] text-left transition-colors ' +
                (on ? 'bg-amber-50 text-amber-900' : 'text-zinc-700 hover:bg-zinc-50')}>
              <span className={'shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ' +
                (on ? 'bg-amber-500 border-amber-500' : 'bg-white border-zinc-300')}>
                {on && (<svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6.5L4.8 9 10 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>)}
              </span>
              {dotMap && <span className={`w-1.5 h-1.5 rounded-full ${dotMap[o]}`} />}
              <span className="flex-1 truncate">{o}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Date range picker with quick-select presets.
 *  - direction="past"        (default) → Today / Last 7 / Last 14 / Last 1 month
 *  - direction="future"               → Today / Next 7 / Next 14 / Next 1 month
 *  - direction="operations"           → This/Next/Last month + Previous (all past) /
 *                                       Next (all future) — calibrated to how
 *                                       staff think about scheduled service events.
 */
export function DatePanel({ from, to, onChange, direction = 'past' }) {
  const presets = useMemo(() => {
    const pad = (n) => String(n).padStart(2, '0')
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    const todayStr = fmt(new Date())
    const shift = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return fmt(d) }
    if (direction === 'operations') {
      const today = new Date()
      const y = today.getFullYear(), m = today.getMonth()
      // JavaScript date math: month 0 = Jan, month -1 wraps to prior Dec; day 0 of
      // month X is the last day of month X-1. So `new Date(y, m+1, 0)` = end of THIS month.
      const thisMonthStart = fmt(new Date(y, m,     1))
      const thisMonthEnd   = fmt(new Date(y, m + 1, 0))
      const nextMonthStart = fmt(new Date(y, m + 1, 1))
      const nextMonthEnd   = fmt(new Date(y, m + 2, 0))
      const lastMonthStart = fmt(new Date(y, m - 1, 1))
      const lastMonthEnd   = fmt(new Date(y, m,     0))
      // "Previous" = everything from the first ever record up through yesterday.
      // "Next"     = everything from today through the last future record.
      // Server treats empty bounds as unbounded, so leaving one end blank works
      // without us needing to query the actual earliest/latest record dates.
      return [
        { label: 'This month', from: thisMonthStart, to: thisMonthEnd },
        { label: 'Next month', from: nextMonthStart, to: nextMonthEnd },
        { label: 'Last month', from: lastMonthStart, to: lastMonthEnd },
        { label: 'Previous',   from: '',             to: shift(-1) },
        { label: 'Next',       from: todayStr,       to: '' },
      ]
    }
    if (direction === 'future') {
      return [
        { label: 'Today',        from: todayStr,   to: todayStr },
        { label: 'Next 7 days',  from: todayStr,   to: shift(6)  },
        { label: 'Next 14 days', from: todayStr,   to: shift(13) },
        { label: 'Next 1 month', from: todayStr,   to: shift(29) },
      ]
    }
    return [
      { label: 'Today',        from: todayStr,   to: todayStr  },
      { label: 'Last 7 days',  from: shift(-6),  to: todayStr  },
      { label: 'Last 14 days', from: shift(-13), to: todayStr  },
      { label: 'Last 1 month', from: shift(-29), to: todayStr  },
    ]
  }, [direction])
  const activeLabel = presets.find((p) => p.from === from && p.to === to)?.label

  return (
    <div className="p-3 min-w-[260px]">
      <div className="grid grid-cols-2 gap-1">
        {presets.map((p) => {
          const on = activeLabel === p.label
          return (
            <button key={p.label} onClick={() => onChange(p.from, p.to)}
              className={'px-2.5 py-1.5 rounded-md text-[12px] font-medium text-left transition-colors ' +
                (on ? 'bg-amber-100/80 text-amber-900 ring-1 ring-amber-200' : 'text-zinc-700 hover:bg-zinc-50 ring-1 ring-transparent')}>
              {p.label}
            </button>
          )
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-zinc-100">
        <label className="block text-[10.5px] uppercase tracking-wider text-zinc-500 font-medium mb-1">From</label>
        <input type="date" value={from} onChange={(e) => onChange(e.target.value, to)}
          className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] font-medium focus:border-amber-500 focus:outline-none" />
        <label className="block text-[10.5px] uppercase tracking-wider text-zinc-500 font-medium mt-2.5 mb-1">To</label>
        <input type="date" value={to} onChange={(e) => onChange(from, e.target.value)}
          className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13px] font-medium focus:border-amber-500 focus:outline-none" />
      </div>
      {(from || to) && (
        <button onClick={() => onChange('', '')} className="mt-3 text-[11.5px] text-zinc-500 hover:text-zinc-900">
          Clear dates
        </button>
      )}
    </div>
  )
}

export function ActiveChip({ icon: Icon, label, value, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 bg-amber-50 ring-1 ring-amber-200/70 rounded-md text-[11.5px] text-zinc-700">
      <Icon size={11} className="text-amber-600" />
      <span className="text-zinc-500">{label}:</span>
      <span className="font-medium text-zinc-900">{value}</span>
      <button onClick={onRemove}
        className="ml-0.5 w-4 h-4 rounded hover:bg-amber-100 flex items-center justify-center text-zinc-500 hover:text-zinc-900"
        aria-label="Remove filter">
        <X size={11} />
      </button>
    </span>
  )
}

export function ViewSwitcher({ value, onChange, views }) {
  return (
    <div className="inline-flex bg-zinc-100/80 ring-1 ring-zinc-200/60 rounded-lg p-0.5">
      {views.map(({ v, label, icon: Icon }) => {
        const on = value === v
        return (
          <button key={v} onClick={() => onChange(v)}
            className={'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12.5px] font-medium transition-colors ' +
              (on ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-900')}>
            <Icon size={13} strokeWidth={2.25} className={on ? 'text-amber-600' : 'text-zinc-400'} />
            {label}
          </button>
        )
      })}
    </div>
  )
}

/* ─────────────────────── toast ─────────────────────── */

/* ─────────────────────── confirm dialog ───────────────────────
 *  Centered modal popup for destructive actions. Replaces the old
 *  "click again to confirm" pattern everywhere it was used.
 *
 *  Usage:
 *    const [open, setOpen] = useState(false)
 *    ...
 *    <ConfirmDialog
 *      open={open}
 *      title="Delete lead"
 *      message="This will permanently remove the lead and all its follow-ups."
 *      confirmLabel="Delete"
 *      tone="danger"           // 'danger' | 'warn' | 'info'   (defaults to 'danger')
 *      onCancel={() => setOpen(false)}
 *      onConfirm={async () => { await api.leads.remove(id); setOpen(false) }}
 *    />
 */
export function ConfirmDialog({
  open,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel  = 'Cancel',
  tone = 'danger',
  onConfirm,
  onCancel,
}) {
  const [busy, setBusy] = useState(false)

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  const palette = {
    danger: {
      ring: 'ring-rose-200/70',
      iconBg: 'bg-rose-50 text-rose-600',
      btn: 'bg-rose-600 hover:bg-rose-700 shadow-sm shadow-rose-500/25',
    },
    warn: {
      ring: 'ring-amber-200/70',
      iconBg: 'bg-amber-50 text-amber-600',
      btn: 'bg-amber-500 hover:bg-amber-600 shadow-sm shadow-amber-500/25',
    },
    info: {
      ring: 'ring-zinc-200',
      iconBg: 'bg-zinc-100 text-zinc-700',
      btn: 'bg-zinc-900 hover:bg-zinc-800',
    },
  }[tone] || {}

  async function handleConfirm() {
    if (busy) return
    try {
      setBusy(true)
      await onConfirm?.()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div onClick={busy ? undefined : onCancel} className="absolute inset-0 bg-zinc-900/40 backdrop-blur-[2px]" />
      <div className={`relative w-full max-w-[400px] rounded-2xl bg-white shadow-2xl ring-1 ${palette.ring}`}>
        <div className="p-5 flex items-start gap-3">
          <div className={`shrink-0 w-9 h-9 rounded-full ${palette.iconBg} flex items-center justify-center`}>
            <AlertTriangle size={18} strokeWidth={2.25} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-semibold text-zinc-900">{title}</h3>
            {message && <div className="mt-1 text-[13px] text-zinc-600 leading-snug">{message}</div>}
          </div>
        </div>
        <div className="px-5 pb-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-[13px] font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className={`px-3.5 py-1.5 rounded-lg text-[13px] font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-60 ${palette.btn}`}
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}


export function Toast({ toast, onDone }) {
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(onDone, 2200)
    return () => clearTimeout(t)
  }, [toast, onDone])
  if (!toast) return null
  return (
    <div key={toast.id} role="status" aria-live="polite"
      className="fixed top-[60px] md:top-5 left-1/2 -translate-x-1/2 z-50 pointer-events-none animate-tt-toast-in">
      <div className="bg-zinc-900 text-white text-[13px] font-medium pl-2.5 pr-3.5 py-2 rounded-full shadow-[0_8px_28px_-6px_rgba(0,0,0,0.35)] flex items-center gap-2 ring-1 ring-zinc-700/40">
        <span className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shrink-0 shadow-sm shadow-amber-500/30">
          <Check size={12} strokeWidth={3} className="text-white" />
        </span>
        {toast.message}
      </div>
    </div>
  )
}


/* ─────────────────────── created-by chip with popover ───────────────────────
 *  Compact pill showing the employee `code` of whoever created a record.
 *  Looks up the id against the live employee directory; falls back to the
 *  denormalized `fallbackCode` column when the directory hasn't loaded yet.
 *
 *  Clicking the chip toggles a small popover with the full timestamp and
 *  employee details. Shows "unknown" when the id isn't in the directory.
 *
 *  Usage:
 *    <CreatedByChip
 *      createdAtUnix={row.created_at}
 *      createdById={row.created_by_xano_id}
 *      fallbackCode={row.created_by_code}    // optional: denorm value from DB
 *    />
 */
export function CreatedByChip({ createdAtUnix, createdById, fallbackCode }) {
  const map = useEmployees()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const closeTimer = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onEsc   = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  if (!createdById && !fallbackCode && !createdAtUnix) return null

  const liveLabel = employeeLabel(map, createdById)
  const code = liveLabel === '…' ? (fallbackCode || '…') : liveLabel
  const emp  = employeeRecord(map, createdById)

  // Hover opens (with small close-delay so users can move into the popover).
  const handleEnter = () => { clearTimeout(closeTimer.current); setOpen(true) }
  const handleLeave = () => { closeTimer.current = setTimeout(() => setOpen(false), 120) }

  return (
    <span
      className="relative inline-flex align-middle"
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Show created info"
        aria-label="Created info"
        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
      >
        <Clock size={12} strokeWidth={2.25} />
      </button>
      {open && (
        <div
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          className="absolute z-50 right-0 top-full mt-1.5 w-[220px] bg-white rounded-xl ring-1 ring-zinc-200 shadow-[0_8px_28px_-6px_rgba(0,0,0,0.18)] p-3 text-[12px]"
        >
          <div className="flex items-start gap-2 mb-2">
            <UserCircle2 size={28} className="text-zinc-300 shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold text-zinc-900 truncate">
                {emp?.name || code || 'unknown'}
              </div>
              <div className="text-[11px] text-zinc-500 tracking-wider uppercase font-medium tabular-nums">
                {emp?.code || fallbackCode || (code === 'unknown' ? '—' : code)}
              </div>
              {emp?.email && (
                <div className="text-[11px] text-zinc-500 truncate" title={emp.email}>{emp.email}</div>
              )}
            </div>
          </div>
          {createdAtUnix ? (
            <div className="pt-2 border-t border-zinc-100 flex items-center gap-1.5 text-[11px] text-zinc-500">
              <Clock size={11} />
              <span className="uppercase tracking-wider font-medium">Created</span>
              <span className="tabular-nums text-zinc-700">{fmtCreatedAt(createdAtUnix)}</span>
            </div>
          ) : null}
        </div>
      )}
    </span>
  )
}

function fmtCreatedAt(unix) {
  if (!unix) return '—'
  const d = new Date(unix * 1000)
  return d.toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })
}


/* ─────────────────────── DoctorCombobox ───────────────────────
 *  Searchable doctor picker backed by the cached /api/doctors list.
 *  Shows profile_img (or initial fallback) + code; `disabledIds` is the
 *  set of IDs that should be greyed-out (e.g. doctor already picked in
 *  the other slot). Selected value = a doctor id (number) or null.
 */
export function DoctorCombobox({ value, onChange, placeholder = 'เลือกหมอ…', disabledIds = [], disabled }) {
  const state = useDoctors()
  const [open, setOpen] = useState(false)
  const [q, setQ]       = useState('')
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onEsc   = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const selected = doctorRecord(state, value)
  const blocked  = new Set(disabledIds.filter((id) => id != null && id !== value).map(Number))

  const filtered = useMemo(() => {
    const all = state?.list || []
    const needle = q.trim().toLowerCase()
    const base = needle
      ? all.filter((d) => (d.code || '').toLowerCase().includes(needle))
      : all
    return base.slice(0, 120)   // cap render cost; doctors > 500
  }, [state, q])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] hover:border-zinc-300 focus:border-amber-500 focus:outline-none text-left disabled:opacity-50"
      >
        <span className="flex items-center gap-2 min-w-0">
          <DoctorAvatar doctor={selected} size={22} />
          <span className={selected ? 'text-zinc-900 truncate' : 'text-zinc-400'}>
            {selected?.code || placeholder}
          </span>
        </span>
        {selected && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onChange(null) }}
            className="text-zinc-400 hover:text-zinc-700 shrink-0"
            aria-label="Clear"
          >
            <X size={13} />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-white rounded-xl ring-1 ring-zinc-200 shadow-[0_8px_28px_-6px_rgba(0,0,0,0.12)] overflow-hidden">
          <div className="relative border-b border-zinc-100">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="พิมพ์ชื่อหมอ…"
              className="w-full pl-9 pr-3 py-2 text-[13px] focus:outline-none"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {!state && (
              <li className="px-3 py-4 text-[12.5px] text-zinc-400 text-center inline-flex items-center justify-center gap-2 w-full">
                <Loader2 size={12} className="animate-spin" /> Loading…
              </li>
            )}
            {state && filtered.length === 0 && (
              <li className="px-3 py-4 text-[12.5px] text-zinc-400 text-center">No doctors match.</li>
            )}
            {filtered.map((d) => {
              const isSelected = value === d.id
              const isBlocked  = blocked.has(d.id)
              return (
                <li key={d.id}>
                  <button
                    type="button"
                    disabled={isBlocked}
                    onClick={() => { onChange(d.id); setOpen(false); setQ('') }}
                    className={'w-full text-left px-3 py-1.5 text-[13px] flex items-center gap-2 ' +
                      (isBlocked
                        ? 'text-zinc-300 cursor-not-allowed'
                        : isSelected ? 'bg-amber-50 text-amber-900' : 'text-zinc-700 hover:bg-zinc-50')}
                    title={isBlocked ? 'หมอท่านนี้ถูกเลือกอีกช่องแล้ว' : undefined}
                  >
                    <DoctorAvatar doctor={d} size={22} />
                    <span className="truncate flex-1">{d.code}</span>
                    {d.spec?.length ? (
                      <span className="text-[10.5px] text-zinc-400 truncate max-w-[120px]">
                        {d.spec.slice(0, 2).join(' · ')}
                      </span>
                    ) : null}
                    {isSelected && <Check size={13} className="text-amber-600 shrink-0" />}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

export function DoctorAvatar({ doctor, size = 24 }) {
  const [broken, setBroken] = useState(false)
  if (doctor?.profile_img && !broken) {
    return (
      <img
        src={doctor.profile_img}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        className="rounded-full object-cover ring-1 ring-zinc-200 shrink-0"
        style={{ width: size, height: size }}
        onError={() => setBroken(true)}
      />
    )
  }
  return (
    <span
      className="rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200/70 inline-flex items-center justify-center font-semibold tracking-wide shrink-0 select-none"
      style={{ width: size, height: size, fontSize: Math.max(9, Math.floor(size * 0.42)) }}
      title={doctor?.code || ''}
    >
      {avatarInitial(doctor?.code)}
    </span>
  )
}

function avatarInitial(code) {
  if (!code) return '?'
  // Doctor codes are "<nickname> <firstname>" — take the first character of
  // the nickname so it's distinct between Ploy/Pang/Pat etc.
  const trimmed = String(code).trim()
  if (!trimmed) return '?'
  return [...trimmed][0] || '?'
}


/* ─────────────────────── shared period filter ───────────────────────
 * Pill-style segmented control used by all three Operations-page charts
 * (activity hero · op-days trend · treatment-income dashboard) so they share
 * one period selection. `size="sm"` is the compact variant for the small hero
 * card; default is the roomier variant used on the wide cards.
 */
import { PERIODS } from '../lib/periods.js'

export function PeriodPills({ value, onChange, size = 'md' }) {
  const btn = size === 'sm' ? 'px-2 py-0.5 text-[10.5px]' : 'px-3 py-1.5 text-[12.5px]'
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg bg-zinc-100/80 p-0.5 ring-1 ring-zinc-200/60">
      {PERIODS.map((p) => {
        const on = p.key === value
        return (
          <button
            key={p.key}
            type="button"
            onClick={() => onChange(p.key)}
            className={
              `${btn} rounded-md font-medium transition-colors whitespace-nowrap ` +
              (on ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-900')
            }
          >
            {p.label}
          </button>
        )
      })}
    </div>
  )
}
